import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Role hierarchy (higher number = more permissions)
 */
const ROLE_HIERARCHY = {
  player: 1,
  team_captain: 2,
  club_admin: 3,
  system_admin: 4,
};

/**
 * Hook to check user permissions based on their roles
 */
export const usePermissions = () => {
  const { userRoles, user } = useAuth();

  const permissions = useMemo(() => {
    // Helper: Check if user has a specific role
    const hasRole = (role, clubId = null, teamId = null) => {
      return userRoles.some((r) => {
        if (r.role !== role) return false;

        // System admin doesn't have club/team constraints
        if (role === 'system_admin') return true;

        // Club admin must match club
        if (role === 'club_admin') {
          return clubId ? r.club_id === clubId : true;
        }

        // Team captain and player must match team
        if (role === 'team_captain' || role === 'player') {
          return teamId ? r.team_id === teamId : true;
        }

        return true;
      });
    };

    // Helper: Check if user has role at least as high as specified
    const hasRoleAtLeast = (minRole) => {
      const minLevel = ROLE_HIERARCHY[minRole] || 0;
      return userRoles.some((r) => (ROLE_HIERARCHY[r.role] || 0) >= minLevel);
    };

    // Helper: Get highest role
    const getHighestRole = () => {
      if (userRoles.length === 0) return null;
      return userRoles.reduce((highest, current) => {
        const currentLevel = ROLE_HIERARCHY[current.role] || 0;
        const highestLevel = ROLE_HIERARCHY[highest.role] || 0;
        return currentLevel > highestLevel ? current : highest;
      });
    };

    // Permission checks
    const isSystemAdmin = () => hasRole('system_admin');
    const isClubAdmin = (clubId = null) => hasRole('club_admin', clubId);
    const isTeamCaptain = (teamId = null) => hasRole('team_captain', null, teamId);
    const isPlayer = (teamId = null) => hasRole('player', null, teamId);

    // Can user access a specific club?
    const canAccessClub = (clubId) => {
      return (
        isSystemAdmin() ||
        userRoles.some((r) => r.club_id === clubId)
      );
    };

    // Can user access a specific team?
    const canAccessTeam = (teamId) => {
      return (
        isSystemAdmin() ||
        userRoles.some((r) => {
          if (r.role === 'club_admin') {
            // Club admin can access all teams in their club
            const team = r.team || {};
            return team.club_id && userRoles.some(
              (cr) => cr.role === 'club_admin' && cr.club_id === team.club_id
            );
          }
          return r.team_id === teamId;
        })
      );
    };

    // Can user manage clubs? (create, edit, delete)
    const canManageClubs = () => isSystemAdmin();

    // Can user manage teams in a club?
    const canManageTeams = (clubId) => {
      return isSystemAdmin() || isClubAdmin(clubId);
    };

    // Can user manage players in a team?
    const canManagePlayers = (teamId) => {
      // System admin can manage everything
      if (isSystemAdmin()) return true;

      // Check if user has direct team captain access
      if (userRoles.some(r => r.role === 'team_captain' && r.team_id === teamId)) {
        return true;
      }

      // Check if user is club admin - they can manage any team in their club
      // Note: We can't check team.club_id here since we don't have team data
      // The ProtectedRoute already verified club admin has access to this team
      if (userRoles.some(r => r.role === 'club_admin')) {
        return true;
      }

      return false;
    };

    // Can user manage matches/seasons in a team?
    const canManageMatches = (teamId) => {
      // System admin can manage everything
      if (isSystemAdmin()) {
        return true;
      }

      // Check if user has direct team captain access
      const isTeamCaptain = userRoles.some(r => r.role === 'team_captain' && r.team_id === teamId);
      if (isTeamCaptain) {
        return true;
      }

      // Check if user is club admin - they can manage any team in their club
      // Note: We can't check team.club_id here since we don't have team data
      // The ProtectedRoute already verified club admin has access to this team
      const isClubAdmin = userRoles.some(r => r.role === 'club_admin');
      if (isClubAdmin) {
        return true;
      }

      return false;
    };

    // Can user invite others?
    const canInviteUsers = (role, clubId = null, teamId = null) => {
      if (role === 'system_admin') {
        return isSystemAdmin();
      }
      if (role === 'club_admin') {
        return isSystemAdmin();
      }
      if (role === 'team_captain' || role === 'player') {
        return isSystemAdmin() || isClubAdmin(clubId);
      }
      return false;
    };

    // Get all clubs user has access to
    const getAccessibleClubs = () => {
      if (isSystemAdmin()) {
        return 'all'; // Special flag meaning all clubs
      }
      const clubIds = new Set();
      userRoles.forEach((r) => {
        if (r.club_id) clubIds.add(r.club_id);
      });
      return Array.from(clubIds);
    };

    // Get all teams user has access to
    const getAccessibleTeams = () => {
      if (isSystemAdmin()) {
        return 'all'; // Special flag meaning all teams
      }
      const teamIds = new Set();
      userRoles.forEach((r) => {
        if (r.role === 'club_admin') {
          // Would need to fetch all teams in club
          // For now, return a flag
          return 'club_admin';
        }
        if (r.team_id) teamIds.add(r.team_id);
      });
      return Array.from(teamIds);
    };

    // Can user permanently delete players (hard delete)?
    // Only club admins and system admins can permanently delete
    const canPermanentlyDeletePlayers = () => {
      return isSystemAdmin() || userRoles.some(r => r.role === 'club_admin');
    };

    return {
      // Role checks
      isSystemAdmin,
      isClubAdmin,
      isTeamCaptain,
      isPlayer,
      hasRole,
      hasRoleAtLeast,
      getHighestRole,

      // Access checks
      canAccessClub,
      canAccessTeam,

      // Permission checks
      canManageClubs,
      canManageTeams,
      canManagePlayers,
      canManageMatches,
      canInviteUsers,
      canPermanentlyDeletePlayers,

      // Helpers
      getAccessibleClubs,
      getAccessibleTeams,
      userRoles,
    };
  }, [userRoles]);

  return permissions;
};

export default usePermissions;
